import { useState, useEffect } from 'react';
import { api } from '../api/client';

const formatDate = (iso) => {
  if (!iso) return '—';
  // Normalise SQLite date string: replace space with T and strip timezone offset
  const normalised = iso.toString().replace(' ', 'T').replace(/ \+00:00$/, 'Z').replace(/\+00:00$/, 'Z');
  const d = new Date(normalised);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' });
};

const friendlyStatus = (status) => {
  if (!status || status === 'queued' || status === 'sent') return 'Sent';
  if (status === 'delivered') return 'Delivered';
  if (status === 'failed' || status === 'undelivered') return 'Failed';
  return status.charAt(0).toUpperCase() + status.slice(1);
};

const statusColour = (status) => {
  if (!status || status === 'queued' || status === 'sent' || status === 'delivered') return 'var(--success)';
  if (status === 'failed' || status === 'undelivered') return 'var(--danger)';
  return 'var(--muted)';
};

export default function History() {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.messages.history()
      .then(setHistory)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Message History</h1>
        <p className="page-subtitle">Your last 50 sent messages</p>
      </div>

      {loading && <p style={{ color: 'var(--muted)' }}>Loading...</p>}

      {!loading && history.length === 0 && (
        <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--muted)' }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>&#9993;</div>
          <p style={{ fontSize: 16 }}>No messages sent yet.</p>
        </div>
      )}

      {!loading && history.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {history.map(m => (
            <div key={m.id} style={{
              background: 'var(--surface)',
              border: '1px solid var(--border)',
              borderRadius: 14,
              padding: '18px 20px',
            }}>
              {/* Header row */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: 10,
                marginBottom: 12,
              }}>
                {/* Plates + status */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <span style={{
                    fontFamily: 'monospace', fontWeight: 700, fontSize: 15,
                    letterSpacing: 2, color: 'var(--accent)',
                    background: 'var(--accent-bg)', border: '1px solid var(--accent-border)',
                    borderRadius: 6, padding: '3px 10px',
                  }}>
                    {m.sender_registration}
                  </span>
                  <span style={{ color: 'var(--muted)', fontSize: 16 }}>&#8594;</span>
                  <span style={{
                    fontFamily: 'monospace', fontWeight: 700, fontSize: 15,
                    letterSpacing: 2, color: 'var(--text)',
                    background: 'var(--surface2)', border: '1px solid var(--border)',
                    borderRadius: 6, padding: '3px 10px',
                  }}>
                    {m.recipient_registration}
                  </span>
                  <span style={{
                    fontSize: 11, fontWeight: 700, letterSpacing: 0.5,
                    color: statusColour(m.status),
                    background: 'rgba(82,255,138,0.08)',
                    border: `1px solid ${statusColour(m.status)}44`,
                    borderRadius: 20, padding: '2px 10px',
                    textTransform: 'uppercase',
                  }}>
                    {friendlyStatus(m.status)}
                  </span>
                </div>
                {/* Date */}
                <div style={{ fontSize: 12, color: 'var(--muted)', whiteSpace: 'nowrap' }}>
                  {formatDate(m.created_at)}
                </div>
              </div>

              {/* Message body */}
              <div style={{
                fontSize: 14,
                color: 'var(--muted)',
                lineHeight: 1.6,
                borderTop: '1px solid var(--border)',
                paddingTop: 10,
              }}>
                {m.message_body}
              </div>

              {/* Credits */}
              <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 8, opacity: 0.6 }}>
                {m.credits_deducted} credits used
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
